import PropTypes from 'prop-types';
import { Editor } from 'react-draft-wysiwyg';
import { ErrorBoundary } from 'react-error-boundary';

/**
 * The shared Draft.js rich-text editor — `react-draft-wysiwyg`'s `Editor`
 * wrapped in an error boundary that resets when the editor state changes. Use
 * for HTML message bodies in the action center and signup creation flows.
 */
const EditorWysiwyg = ({
  editorState,
  handleEditorError,
  setEditorStateChange,
  inlineOptions,
  listOptions,
  options,
  placeholder,
}) => (
  <ErrorBoundary
    fallback={<>An error occurred</>}
    onError={handleEditorError}
    resetKeys={[editorState]}
  >
    <Editor
      editorClassName="wysiwyg-editor"
      editorState={editorState}
      editorStyle={{ padding: '0 1rem 0' }}
      wrapperClassName="wysiwyg-wrapper"
      textAlignment="left"
      onEditorStateChange={state => setEditorStateChange(state)}
      placeholder={placeholder}
      toolbar={{
        inline: {
          options: inlineOptions,
        },
        list: {
          options: listOptions,
        },
        options,
      }}
    />
  </ErrorBoundary>
);

EditorWysiwyg.defaultProps = {
  inlineOptions: ['bold', 'italic', 'underline'],
  listOptions: ['unordered', 'ordered'],
  options: [
    'inline',
    'blockType',
    'fontSize',
    'fontFamily',
    'list',
    'textAlign',
    'colorPicker',
    'link',
    'embedded',
    'emoji',
    'image',
    'remove',
    'history',
  ],
};

EditorWysiwyg.defaultProps = {
  editorState: null,
  placeholder: null,
};

EditorWysiwyg.propTypes = {
  editorState: PropTypes.shape({
    getCurrentContent: PropTypes.func,
  }),
  /** Called by the error boundary when the editor throws during render. */
  handleEditorError: PropTypes.func.isRequired,
  /** Buttons shown in the toolbar's inline-style dropdown (bold, italic, ...). */
  inlineOptions: PropTypes.arrayOf(PropTypes.string),
  /** Buttons shown in the toolbar's list dropdown (ordered, unordered). */
  listOptions: PropTypes.arrayOf(PropTypes.string),
  /** Top-level toolbar sections to display (inline, list, link, ...). */
  options: PropTypes.arrayOf(PropTypes.string),
  placeholder: PropTypes.string,
  /** Called with the new Draft.js `EditorState` on every edit. */
  setEditorStateChange: PropTypes.func.isRequired,
};

export default EditorWysiwyg;
